import { loadScheduleDocuments } from './schedule-data.mjs';

export function initSchedule({ siteConfig, client, formatTime }) {
  const root = document.getElementById('personal-schedule');
  const offcanvas = document.getElementById('schedule-offcanvas');
  const trigger = document.getElementById('my-schedule-button');
  const heading = document.getElementById('schedule-offcanvas-title');
  const storageKey = `schedule-link:${siteConfig.key}:${siteConfig.typesenseCollection}`;
  let token = new URLSearchParams(location.hash.slice(1)).get('schedule');
  if (!token) {
    try {
      token = localStorage.getItem(storageKey);
    } catch {
      /* Storage is optional. */
    }
  }
  let schedule = null;
  let busy = true;
  const openOnLoad = Boolean(
    new URLSearchParams(location.hash.slice(1)).get('schedule')
  );
  let open = false;
  let generation = 0;
  root.innerHTML = `
    <div class="schedule-toolbar">
      <button type="button" class="btn btn-outline-secondary" data-action="edit" hidden>Copy private editing link</button>
      <button type="button" class="btn btn-outline-secondary" data-action="share" hidden>Copy read-only link</button>
      <button type="button" class="btn btn-outline-secondary" data-action="refresh">Refresh schedule</button>
      <button type="button" class="btn btn-outline-secondary" data-action="new" hidden>Create my own schedule</button>
    </div>
    <p class="schedule-status" role="status" aria-live="polite"></p>
    <div class="schedule-content" hidden><p class="schedule-help"></p><div class="schedule-list"></div></div>
    <div class="schedule-link" hidden><label>Schedule link <input type="text" readonly class="form-control"></label></div>`;
  const status = root.querySelector('.schedule-status');
  const list = root.querySelector('.schedule-list');
  const button = (action) => root.querySelector(`[data-action="${action}"]`);
  function updateButtons() {
    document.querySelectorAll('[data-session-id]').forEach((el) => {
      const selected = Boolean(
        schedule?.sessions.includes(el.dataset.sessionId)
      );
      const label = selected ? 'Remove from my schedule' : 'Add to my schedule';
      el.setAttribute('aria-label', label);
      el.title = label;
      let icon = el.querySelector('i');
      if (!icon) {
        icon = document.createElement('i');
        icon.setAttribute('aria-hidden', 'true');
        el.replaceChildren(icon);
      }
      icon.className = selected
        ? 'bi bi-bookmark-dash-fill'
        : 'bi bi-bookmark-plus';
      el.setAttribute('aria-pressed', String(selected));
      el.disabled =
        busy || Boolean(token && !schedule) || schedule?.editable === false;
    });
    heading.textContent = `${
      schedule?.editable === false ? 'Shared Schedule' : 'My Schedule'
    } (${schedule?.sessions.length || 0})`;
    trigger.setAttribute('aria-expanded', String(open));
    button('edit').hidden = !schedule?.editable;
    button('share').hidden = !schedule;
    button('new').hidden = !(
      schedule?.editable === false ||
      (token && !schedule)
    );
    for (const el of root.querySelectorAll('[data-action]')) el.disabled = busy;
    root.querySelector('.schedule-content').hidden = !open;
    root.querySelector('.schedule-help').textContent =
      schedule?.editable === false
        ? 'This is a read-only schedule. Refresh to see the owner’s latest choices.'
        : 'Close this panel to choose sessions from the programme. Keep your private editing link to open and edit your schedule on another device. Anyone with that link can edit it.';
  }
  async function api(method, payload) {
    const response = await fetch(
      method === 'POST' ? '/api/schedules' : '/api/schedules/current',
      {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(payload ? { body: JSON.stringify(payload) } : {}),
      }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Unable to load schedule.');
    return data;
  }
  function remember() {
    if (schedule?.editable) {
      try {
        localStorage.setItem(storageKey, token);
      } catch {
        /* Link still works. */
      }
    }
  }
  function link(value) {
    const url = new URL(location.href);
    url.searchParams.set('site', siteConfig.key);
    url.hash = new URLSearchParams({ schedule: value }).toString();
    return url.href;
  }
  async function create() {
    const data = await api('POST', { site: siteConfig.key });
    token = data.token;
    schedule = data;
    history.replaceState(null, '', link(token));
    remember();
  }
  async function renderList() {
    const current = ++generation;
    list.replaceChildren();
    if (!open) return;
    if (!schedule?.sessions.length) {
      list.textContent = 'No sessions selected yet.';
      return;
    }
    list.textContent = 'Loading selected sessions…';
    try {
      const ids = [...schedule.sessions];
      const documents = await loadScheduleDocuments(
        client,
        siteConfig.typesenseCollection,
        ids
      );
      if (current !== generation) return;
      list.replaceChildren();
      const found = new Set(documents.map((doc) => String(doc.id)));
      for (const doc of [
        ...documents,
        ...ids
          .filter((id) => !found.has(id))
          .map((id) => ({ id, panel_title: 'Session no longer available' })),
      ]) {
        const article = document.createElement('article');
        article.className = 'schedule-card';
        const title = document.createElement('h3');
        title.textContent = doc.panel_title;
        const details = document.createElement('p');
        details.textContent = [
          doc.date,
          doc.date && formatTime(doc),
          doc.room,
          doc.building,
        ]
          .filter(Boolean)
          .join(' · ');
        article.append(title, details);
        if (schedule.editable) {
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'btn btn-outline-secondary btn-sm';
          remove.dataset.sessionId = String(doc.id);
          article.append(remove);
        }
        list.append(article);
      }
      updateButtons();
    } catch (error) {
      if (current === generation)
        list.textContent = `Unable to load session details. ${error.message} Use Refresh schedule to retry.`;
    }
  }
  async function run(action) {
    if (busy) return;
    busy = true;
    status.textContent = '';
    updateButtons();
    try {
      await action();
    } catch (error) {
      status.textContent = error.message || 'Request failed. Please try again.';
      window.bootstrap?.Offcanvas.getOrCreateInstance(offcanvas).show();
    } finally {
      busy = false;
      updateButtons();
    }
  }
  async function load() {
    if (!token) return;
    const data = await api('GET');
    if (data.site !== siteConfig.key) {
      const url = new URL(location.href);
      url.searchParams.set('site', data.site);
      url.hash = new URLSearchParams({ schedule: token }).toString();
      location.replace(url.href);
      return;
    }
    schedule = data;
    remember();
    await renderList();
  }
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-session-id]');
    if (!el || el.disabled) return;
    run(async () => {
      if (!schedule) await create();
      schedule = await api('PATCH', {
        sessionId: el.dataset.sessionId,
        selected: !schedule.sessions.includes(el.dataset.sessionId),
      });
      status.textContent = 'Schedule saved.';
      if (open) await renderList();
      else window.bootstrap?.Offcanvas.getOrCreateInstance(offcanvas).show();
    });
  });
  root.addEventListener('click', (event) => {
    const action = event.target.closest('[data-action]')?.dataset.action;
    if (!action) return;
    run(async () => {
      if (action === 'refresh') {
        await load();
      }
      if (action === 'new') {
        await create();
        open = true;
        await renderList();
      }
      if (action === 'edit' || action === 'share') {
        const value = link(
          action === 'edit' ? token : schedule.readToken || token
        );
        const field = root.querySelector('.schedule-link');
        field.hidden = false;
        field.querySelector('input').value = value;
        try {
          await navigator.clipboard.writeText(value);
          status.textContent = 'Link copied.';
        } catch {
          field.querySelector('input').focus();
          field.querySelector('input').select();
          status.textContent = 'Copy the link from the field below.';
        }
      }
    });
  });
  offcanvas.addEventListener('show.bs.offcanvas', () => {
    open = true;
    updateButtons();
    renderList();
  });
  offcanvas.addEventListener('hidden.bs.offcanvas', () => {
    open = false;
    generation++;
    updateButtons();
  });
  window.addEventListener('hashchange', () => location.reload());
  busy = false;
  run(load);
  if (openOnLoad)
    window.bootstrap?.Offcanvas.getOrCreateInstance(offcanvas).show();
  return { updateButtons };
}
